import { EventEmitter } from '../events/MapEvent';
import { MIDiagnostics } from '../diagnostics/MIDiagnostics';
import { MIEntityCollection, MIDataSourceCollection, MIImageryLayerCollection } from './MICollections';
import { MIClockController } from './MIClockController';
import type { MICameraOrientation } from '../geomath/MIGeoMath';

import { getCesium, setCesium } from '../util/cesium';
import { GISToolBar, type GISToolBarOffset, type GISToolBarOptions } from '../toolbar/GISToolBar';
// Module-local proxy that always defers to the centralised resolver in
// `util/cesium`, so MISceneView and MIClockController never diverge on
// which engine instance they see (e.g. when tests swap it in).
let Cesium: any = getCesium();
/** @internal — test-only hook to swap the unified Cesium binding. */
export function __setCesiumForTests(c: any): void { setCesium(c); Cesium = c; }
try {
  if (!Cesium) Cesium = require('cesium');
  if (Cesium) setCesium(Cesium);
} catch {
  try {
    Cesium = (globalThis as any).Cesium || null;
  } catch {
    Cesium = null;
  }
}

export type HeightReference = 'absolute' | 'clamp-to-ground' | 'relative-to-ground' | 'clamp-to-3dtiles' | 'relative-to-3dtiles';
export type NavigationMode = 'orbit' | 'walk' | 'fly' | 'first-person';

/**
 * Engine-agnostic pick contract. Every MISceneView pick method returns this
 * shape — consumers never need to deal with Cesium `Entity` / `Cesium3DTileFeature`
 * etc. directly. `raw` is provided as an escape hatch (mirrors the deprecation
 * pattern used for `unstable_getCesiumViewer`).
 */
export interface MIPickResult {
  /** Best-effort categorisation of what was picked. */
  kind: 'entity' | 'primitive' | 'tileset' | 'imagery' | 'unknown';
  /** Screen-space pixel that produced this pick. */
  pixel: [number, number];
  /** Geographic position [lon, lat, alt] under the cursor, when resolvable. */
  position: [number, number, number] | null;
  /** Owning MIGIS Layer instance, when the pick came from one our translators. */
  layer: any | null;
  /** Stable id for the picked feature/entity, when available. */
  id: string | null;
  /** Raw native pick object — escape hatch, do not depend on. */
  raw: any;
}

export interface CameraOptions {
  position: [number, number, number];
  heading: number;
  tilt: number;
  roll?: number;
  fov?: number;
}

export interface CameraState extends CameraOptions {}

export interface TerrainOptions {
  source: any;
  exaggeration?: number;
  meshQuality?: 'low' | 'medium' | 'high';
  enableLighting?: boolean;
  depthTest?: boolean;
  opacity?: number;
}

export interface MIAtmosphereOptions {
  enabled?: boolean;
  rayleighScattering?: [number, number, number];
  mieScattering?: [number, number, number];
  rayleighScaleHeight?: number;
  mieScaleHeight?: number;
  fog?: MIFogOptions;
}

export interface MIFogOptions {
  enabled?: boolean;
  density?: number;
  color?: string;
  minimumBrightness?: number;
  screenSpaceErrorFactor?: number;
}

export interface MIEnvironmentOptions {
  skyBox?: boolean | { sources: Record<string, string> };
  skyAtmosphere?: boolean;
  sun?: boolean;
  moon?: boolean;
  stars?: boolean;
  groundColor?: string;
}

export interface MIUndergroundOptions {
  enabled?: boolean;
  transparency?: number;
  fadeDistance?: number;
}

export interface MIPerformanceOptions {
  gpuMemoryLimit?: number;
  maximumScreenSpaceError?: number;
  tileCacheSize?: number;
  fpsTarget?: number;
  dynamicScreenSpaceError?: boolean;
  fogEnabled?: boolean;
  lodTransitionTime?: number;
  msaaSamples?: number;
  requestRenderMode?: boolean;
}

export interface MILightingOptions {
  type?: 'sun' | 'fixed' | 'custom';
  dateTime?: Date;
  timeZone?: string;
  enableSunTracking?: boolean;
  sunTrackingSpeed?: number;
  azimuth?: number;
  elevation?: number;
  ambientIntensity?: number;
  ambientColor?: string;
  directionalIntensity?: number;
  directionalColor?: string;
  shadows?: MIShadowOptions;
  additionalLights?: MILight[];
}

export interface MIShadowOptions {
  enabled: boolean;
  type?: 'hard' | 'soft';
  cascades?: 1 | 2 | 3 | 4;
  resolution?: 512 | 1024 | 2048 | 4096;
  softness?: number;
  bias?: number;
  normalBias?: number;
  maxDistance?: number;
  fadeDistance?: number;
  darkness?: number;
  terrainCastShadows?: boolean;
  terrainReceiveShadows?: boolean;
}

export interface MILight {
  type: 'point' | 'spot' | 'directional';
  color?: string;
  intensity?: number;
  castShadows?: boolean;
  position?: [number, number, number];
  range?: number;
  decay?: number;
  direction?: [number, number, number];
  innerConeAngle?: number;
  outerConeAngle?: number;
  azimuth?: number;
  elevation?: number;
}

export interface MISceneViewOptions {
  target: string | HTMLElement;
  mode?: 'globe' | 'local';
  terrain?: TerrainOptions;
  atmosphere?: boolean | MIAtmosphereOptions;
  camera?: CameraOptions;
  lighting?: MILightingOptions;
  qualityProfile?: 'low' | 'medium' | 'high';
  navigationMode?: NavigationMode;
  underground?: MIUndergroundOptions;
  environment?: MIEnvironmentOptions;
  performance?: MIPerformanceOptions;
  postProcessing?: any;
}

interface StoredLight extends MILight {
  _id: string;
  _cesiumLight?: any;
}

let lightIdCounter = 0;
function generateLightId(): string {
  return `light_${++lightIdCounter}_${Date.now()}`;
}

let effectIdCounter = 0;
function generateEffectId(): string {
  return `effect_${++effectIdCounter}_${Date.now()}`;
}

export class MISceneView extends EventEmitter {
  private _container: HTMLElement;
  private _mode: string;
  private _camera: CameraState;
  private _layers: any[] = [];
  private _initialized: boolean = false;
  private _cesiumAvailable: boolean = false;
  private _viewer: any = null;
  private _navigationMode: NavigationMode = 'orbit';
  private _sunTracking: boolean = false;
  private _sunTrackingInterval: ReturnType<typeof setInterval> | null = null;
  private _sunTrackingSpeed: number = 1;
  private _lightingTime: Date = new Date();
  private _lights: StoredLight[] = [];
  private _shadowOptions: MIShadowOptions = { enabled: false };
  private _weatherEffects: Map<string, any> = new Map();
  private _waterSurfaces: Map<string, any> = new Map();
  private _postProcessing: any = null;
  private _terrainOptions: TerrainOptions | null = null;
  private _atmosphereOptions: boolean | MIAtmosphereOptions = false;
  private _environmentOptions: MIEnvironmentOptions = {};
  private _undergroundOptions: MIUndergroundOptions = { enabled: false };
  private _performanceOptions: MIPerformanceOptions = {};
  private _orbitHandle: ReturnType<typeof setInterval> | null = null;
  private _destroyed: boolean = false;
  private _gisToolBar: GISToolBar | null = null;
  private _adoptedRawViewer: any = null;
  private _ownsCesiumViewer = true;

  /**
   * Adopt an existing Cesium Viewer for an in-product integration. The native
   * viewer is deliberately `unknown` to keep Cesium types out of the SDK API.
   * The adopted scene does not destroy the supplied viewer.
   */
  static adoptCesiumViewer(viewer: unknown, target?: HTMLElement): MISceneView {
    const nativeViewer = viewer as any;
    const container = target ?? nativeViewer.container;
    if (!container) throw new Error('Cannot adopt a Cesium Viewer without its container.');
    const scene = new MISceneView({
      target: container,
      __adoptCesiumViewer: nativeViewer,
    } as MISceneViewOptions & { __adoptCesiumViewer: any });
    scene._adoptedRawViewer = nativeViewer;
    scene._syncAdoptedRawLayers();
    return scene;
  }

  constructor(options: MISceneViewOptions & { __adoptCesiumViewer?: any }) {
    super();

    const container =
      typeof options.target === 'string'
        ? document.getElementById(options.target)
        : options.target;

    if (!container) throw new Error(`Target element not found: ${options.target}`);
    this._container = container;
    this._mode = options.mode || 'globe';
    this._navigationMode = options.navigationMode || 'orbit';

    this._camera = options.camera || {
      position: [0, 0, 10000000],
      heading: 0,
      tilt: 0,
    };

    if (options.lighting?.dateTime) {
      this._lightingTime = new Date(options.lighting.dateTime);
    }
    if (options.lighting?.shadows) {
      this._shadowOptions = { ...options.lighting.shadows };
    }
    if (options.lighting?.additionalLights) {
      for (const light of options.lighting.additionalLights) {
        this._lights.push({ ...light, _id: generateLightId() });
      }
    }

    try {
      if (!Cesium) {
        Cesium = (globalThis as any).Cesium || null;
        if (Cesium) setCesium(Cesium);
      }
      if (!Cesium) {
        throw new Error('CesiumJS is not available');
      }

      if (options.__adoptCesiumViewer) {
        this._viewer = options.__adoptCesiumViewer;
        this._ownsCesiumViewer = false;
        this._cesiumAvailable = true;
        this._setupCesiumEvents();
      } else {
      // Instantiate every widget by default so the per-widget setters
      // (setBaseLayerPickerVisible / setTimelineVisible / etc.) can hide and
      // re-show them at runtime. Callers who want a clean viewer just call
      // the visibility setters with `false` (we apply that immediately below).
      // Instantiate every widget exposed by setWidgetVisible() so all
      // public toggles (including vrButton + projectionPicker) target a
      // real, instantiated container at runtime. `scene3DOnly: false`
      // is required for projectionPicker to be created by Cesium.
      this._viewer = new Cesium.Viewer(this._container, {
        animation: true,
        baseLayerPicker: true,
        fullscreenButton: true,
        geocoder: true,
        homeButton: true,
        infoBox: true,
        sceneModePicker: true,
        selectionIndicator: true,
        timeline: true,
        navigationHelpButton: true,
        vrButton: true,
        projectionPicker: true,
        scene3DOnly: false,
        requestRenderMode: options.performance?.requestRenderMode ?? false,
        msaaSamples: options.performance?.msaaSamples ?? 1,
      });
      // Match historical default: hide every widget; consumers opt-in via
      // setXxxVisible(true). Widgets remain instantiated and toggleable.
      this.setAnimationVisible(false);
      this.setBaseLayerPickerVisible(false);
      this.setFullscreenButtonVisible(false);
      this.setGeocoderVisible(false);
      this.setHomeButtonVisible(false);
      this.setInfoBoxVisible(false);
      this.setSceneModePickerVisible(false);
      this.setSelectionIndicatorVisible(false);
      this.setTimelineVisible(false);
      this.setNavigationHelpButtonVisible(false);
      this.setVrButtonVisible(false);
      this.setProjectionPickerVisible(false);

      this._cesiumAvailable = true;
      this._setupCesiumEvents();
      }

      if (options.terrain) {
        this.setTerrain(options.terrain);
      }
      if (options.atmosphere !== undefined) {
        this.setAtmosphere(options.atmosphere);
      }
      if (options.environment) {
        this.setEnvironment(options.environment);
      }
      if (options.underground) {
        this.setUnderground(options.underground);
      }
      if (options.performance) {
        this._applyPerformanceOptions(options.performance);
      }
      if (options.camera) {
        this.setCamera(options.camera, false);
      }
      if (options.lighting) {
        this._applyLightingOptions(options.lighting);
      }
      if (options.qualityProfile) {
        this._applyQualityProfile(options.qualityProfile);
      }
      if (options.postProcessing) {
        this.setPostProcessing(options.postProcessing);
      }
      if (options.lighting?.enableSunTracking) {
        this._sunTrackingSpeed = options.lighting.sunTrackingSpeed ?? 1;
        this.enableSunTracking(this._sunTrackingSpeed);
      }
    } catch (err) {
      this._cesiumAvailable = false;
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }

    this._initialized = true;
    this.emit('ready');
  }

  private _setupCesiumEvents(): void {
    if (!this._viewer) return;

    const scene = this._viewer.scene;
    const camera = this._viewer.camera;

    camera.changed.addEventListener(() => {
      this._syncCameraFromCesium();
      this.emit('camera-change', { camera: this.getCamera() });
    });

    camera.moveEnd.addEventListener(() => {
      this._syncCameraFromCesium();
      this.emit('camera-change', { camera: this.getCamera() });
    });

    const handler = new Cesium.ScreenSpaceEventHandler(scene.canvas);

    handler.setInputAction((movement: any) => {
      const cartesian = scene.pickPosition(movement.position);
      const coord = this._cartesianToLonLatAlt(cartesian);
      this.emit('click', {
        coordinate: coord,
        pixel: [movement.position.x, movement.position.y],
        originalEvent: null,
        dragging: false,
      });
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction((movement: any) => {
      const cartesian = scene.pickPosition(movement.position);
      const coord = this._cartesianToLonLatAlt(cartesian);
      this.emit('dblclick', {
        coordinate: coord,
        pixel: [movement.position.x, movement.position.y],
        originalEvent: null,
        dragging: false,
      });
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    handler.setInputAction((movement: any) => {
      const cartesian = scene.pickPosition(movement.endPosition);
      const coord = this._cartesianToLonLatAlt(cartesian);
      this.emit('pointermove', {
        coordinate: coord,
        pixel: [movement.endPosition.x, movement.endPosition.y],
        originalEvent: null,
        dragging: false,
      });
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }

  private _syncCameraFromCesium(): void {
    if (!this._cesiumAvailable || !this._viewer) return;
    try {
      const cam = this._viewer.camera;
      const carto = Cesium.Cartographic.fromCartesian(cam.positionWC);
      this._camera = {
        position: [
          Cesium.Math.toDegrees(carto.longitude),
          Cesium.Math.toDegrees(carto.latitude),
          carto.height,
        ],
        heading: Cesium.Math.toDegrees(cam.heading),
        tilt: Cesium.Math.toDegrees(cam.pitch),
        roll: Cesium.Math.toDegrees(cam.roll),
        fov: Cesium.Math.toDegrees(cam.frustum.fov || 60),
      };
    } catch {
    }
  }

  private _cartesianToLonLatAlt(cartesian: any): [number, number, number] | null {
    if (!cartesian || !Cesium) return null;
    try {
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      return [
        Cesium.Math.toDegrees(carto.longitude),
        Cesium.Math.toDegrees(carto.latitude),
        carto.height,
      ];
    } catch {
      return null;
    }
  }

  private _lonLatAltToCartesian(lon: number, lat: number, alt: number): any {
    if (!Cesium) return null;
    return Cesium.Cartesian3.fromDegrees(lon, lat, alt);
  }

  private _applyPerformanceOptions(perf: MIPerformanceOptions): void {
    if (!this._cesiumAvailable || !this._viewer) return;
    this._performanceOptions = { ...perf };
    try {
      const scene = this._viewer.scene;
      const globe = scene.globe;
      if (perf.maximumScreenSpaceError !== undefined) {
        globe.maximumScreenSpaceError = perf.maximumScreenSpaceError;
      }
      if (perf.tileCacheSize !== undefined) {
        globe.tileCacheSize = perf.tileCacheSize;
      }
      if (perf.dynamicScreenSpaceError !== undefined) {
        globe.dynamicScreenSpaceError = perf.dynamicScreenSpaceError;
      }
      if (perf.fogEnabled !== undefined) {
        scene.fog.enabled = perf.fogEnabled;
      }
      if (perf.requestRenderMode !== undefined) {
        scene.requestRenderMode = perf.requestRenderMode;
      }
    } catch {
    }
  }

  private _applyQualityProfile(profile: 'low' | 'medium' | 'high'): void {
    const profiles: Record<string, MIPerformanceOptions> = {
      low: { maximumScreenSpaceError: 8, tileCacheSize: 50, dynamicScreenSpaceError: true, fogEnabled: true, msaaSamples: 1 },
      medium: { maximumScreenSpaceError: 4, tileCacheSize: 100, dynamicScreenSpaceError: true, fogEnabled: true, msaaSamples: 2 },
      high: { maximumScreenSpaceError: 2, tileCacheSize: 200, dynamicScreenSpaceError: false, fogEnabled: false, msaaSamples: 4 },
    };
    this._applyPerformanceOptions(profiles[profile] || profiles.medium);
  }

  private _applyLightingOptions(lighting: MILightingOptions): void {
    if (!this._cesiumAvailable || !this._viewer) return;
    try {
      const scene = this._viewer.scene;
      if (lighting.type === 'sun' || lighting.type === undefined) {
        scene.globe.enableLighting = true;
      } else if (lighting.type === 'fixed') {
        scene.globe.enableLighting = false;
      }

      if (lighting.dateTime) {
        this._lightingTime = new Date(lighting.dateTime);
        this._viewer.clock.currentTime = Cesium.JulianDate.fromDate(this._lightingTime);
      }

      if (lighting.shadows) {
        this.setShadowOptions(lighting.shadows);
      }

      if (lighting.additionalLights) {
        for (const light of lighting.additionalLights) {
          this.addLight(light);
        }
      }
    } catch {
    }
  }

  setCamera(options: CameraOptions, animate: boolean = false): void {
    this._camera = { ...options };

    if (!this._cesiumAvailable || !this._viewer) {
      this.emit('camera-change', { camera: this.getCamera() });
      return;
    }

    try {
      const destination = this._lonLatAltToCartesian(
        options.position[0],
        options.position[1],
        options.position[2]
      );
      const orientation = {
        heading: Cesium.Math.toRadians(options.heading),
        pitch: Cesium.Math.toRadians(options.tilt),
        roll: Cesium.Math.toRadians(options.roll || 0),
      };

      if (animate) {
        this._viewer.camera.flyTo({
          destination,
          orientation,
          duration: 1.5,
        });
      } else {
        this._viewer.camera.setView({
          destination,
          orientation,
        });
      }

      this.emit('camera-change', { camera: this.getCamera() });
    } catch {
      this.emit('camera-change', { camera: this.getCamera() });
    }
  }

  getCamera(): CameraState {
    if (this._cesiumAvailable && this._viewer) {
      this._syncCameraFromCesium();
    }
    return { ...this._camera };
  }

  async flyTo(target: CameraOptions, duration: number = 2000): Promise<void> {
    if (!this._cesiumAvailable || !this._viewer) {
      return new Promise((resolve) => {
        setTimeout(() => {
          this._camera = { ...target };
          this.emit('camera-change', { camera: this.getCamera() });
          resolve();
        }, duration);
      });
    }

    return new Promise((resolve, reject) => {
      try {
        const destination = this._lonLatAltToCartesian(
          target.position[0],
          target.position[1],
          target.position[2]
        );
        const orientation = {
          heading: Cesium.Math.toRadians(target.heading),
          pitch: Cesium.Math.toRadians(target.tilt),
          roll: Cesium.Math.toRadians(target.roll || 0),
        };

        this._viewer.camera.flyTo({
          destination,
          orientation,
          duration: duration / 1000,
          complete: () => {
            this._syncCameraFromCesium();
            this.emit('camera-change', { camera: this.getCamera() });
            resolve();
          },
          cancel: () => {
            resolve();
          },
        });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  lookAt(target: [number, number, number]): void {
    if (!this._cesiumAvailable || !this._viewer) {
      this._camera.position = [...target];
      this.emit('camera-change', { camera: this.getCamera() });
      return;
    }

    try {
      const targetCartesian = this._lonLatAltToCartesian(target[0], target[1], target[2]);
      const offset = new Cesium.HeadingPitchRange(
        Cesium.Math.toRadians(this._camera.heading),
        Cesium.Math.toRadians(this._camera.tilt),
        target[2] || 1000
      );
      this._viewer.camera.lookAt(targetCartesian, offset);
      this._syncCameraFromCesium();
      this.emit('camera-change', { camera: this.getCamera() });
    } catch {
      this._camera.position = [...target];
      this.emit('camera-change', { camera: this.getCamera() });
    }
  }

  orbitAround(
    center: [number, number, number],
    options?: { duration?: number; angle?: number; clockwise?: boolean }
  ): { stop: () => void } {
    const duration = options?.duration ?? 10000;
    const totalAngle = options?.angle ?? 360;
    const clockwise = options?.clockwise ?? true;
    const direction = clockwise ? 1 : -1;
    const stepMs = 50;
    const steps = duration / stepMs;
    const anglePerStep = (totalAngle / steps) * direction;
    let currentStep = 0;

    if (this._orbitHandle) {
      clearInterval(this._orbitHandle);
    }

    if (this._cesiumAvailable && this._viewer) {
      try {
        const centerCartesian = this._lonLatAltToCartesian(center[0], center[1], center[2]);
        this._viewer.camera.lookAt(
          centerCartesian,
          new Cesium.HeadingPitchRange(
            Cesium.Math.toRadians(this._camera.heading),
            Cesium.Math.toRadians(this._camera.tilt),
            Cesium.Cartesian3.distance(this._viewer.camera.positionWC, centerCartesian)
          )
        );
      } catch {
      }
    }

    this._orbitHandle = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        if (this._orbitHandle) clearInterval(this._orbitHandle);
        this._orbitHandle = null;
        return;
      }

      if (this._cesiumAvailable && this._viewer) {
        try {
          this._viewer.camera.rotateRight(Cesium.Math.toRadians(anglePerStep));
          this._syncCameraFromCesium();
          this.emit('camera-change', { camera: this.getCamera() });
        } catch {
        }
      } else {
        this._camera.heading = (this._camera.heading + anglePerStep) % 360;
        this.emit('camera-change', { camera: this.getCamera() });
      }
    }, stepMs);

    return {
      stop: () => {
        if (this._orbitHandle) {
          clearInterval(this._orbitHandle);
          this._orbitHandle = null;
        }
        if (this._cesiumAvailable && this._viewer) {
          try {
            this._viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
          } catch {
          }
        }
      },
    };
  }

  setNavigationMode(mode: NavigationMode): void {
    this._navigationMode = mode;

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const scene = this._viewer.scene;
      const controller = scene.screenSpaceCameraController;
      switch (mode) {
        case 'orbit':
          controller.enableRotate = true;
          controller.enableTranslate = true;
          controller.enableZoom = true;
          controller.enableTilt = true;
          controller.enableLook = false;
          break;
        case 'walk':
          controller.enableRotate = false;
          controller.enableTranslate = true;
          controller.enableZoom = true;
          controller.enableTilt = true;
          controller.enableLook = true;
          break;
        case 'fly':
          controller.enableRotate = true;
          controller.enableTranslate = true;
          controller.enableZoom = true;
          controller.enableTilt = true;
          controller.enableLook = true;
          break;
        case 'first-person':
          controller.enableRotate = false;
          controller.enableTranslate = false;
          controller.enableZoom = false;
          controller.enableTilt = true;
          controller.enableLook = true;
          break;
      }
    } catch {
    }
  }

  getNavigationMode(): NavigationMode {
    return this._navigationMode;
  }

  setTerrain(options: TerrainOptions): void {
    this._terrainOptions = { ...options };

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const scene = this._viewer.scene;
      const source = options.source;

      if (source && typeof source === 'object' && typeof source.then === 'function') {
        source.then((provider: any) => {
          scene.terrainProvider = provider;
        }).catch((err: any) => {
          this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
        });
      } else if (source instanceof Object && source.url) {
        Cesium.CesiumTerrainProvider.fromUrl(source.url, {
          requestVertexNormals: options.enableLighting ?? true,
        }).then((provider: any) => {
          scene.terrainProvider = provider;
        }).catch((err: any) => {
          this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
        });
      } else if (typeof source === 'string') {
        Cesium.CesiumTerrainProvider.fromUrl(source, {
          requestVertexNormals: options.enableLighting ?? true,
        }).then((provider: any) => {
          scene.terrainProvider = provider;
        }).catch((err: any) => {
          this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
        });
      } else if (source) {
        scene.terrainProvider = source;
      }

      if (options.exaggeration !== undefined) {
        scene.verticalExaggeration = options.exaggeration;
      }

      if (options.enableLighting !== undefined) {
        scene.globe.enableLighting = options.enableLighting;
      }

      if (options.depthTest !== undefined) {
        scene.globe.depthTestAgainstTerrain = options.depthTest;
      }
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  setAtmosphere(options: boolean | MIAtmosphereOptions): void {
    this._atmosphereOptions = options;

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const scene = this._viewer.scene;

      if (typeof options === 'boolean') {
        scene.skyAtmosphere.show = options;
        scene.fog.enabled = options;
        return;
      }

      if (options.enabled !== undefined) {
        scene.skyAtmosphere.show = options.enabled;
      }

      if (options.fog) {
        scene.fog.enabled = options.fog.enabled ?? true;
        if (options.fog.density !== undefined) {
          scene.fog.density = options.fog.density;
        }
        if (options.fog.minimumBrightness !== undefined) {
          scene.fog.minimumBrightness = options.fog.minimumBrightness;
        }
        if (options.fog.screenSpaceErrorFactor !== undefined) {
          scene.fog.screenSpaceErrorFactor = options.fog.screenSpaceErrorFactor;
        }
      }
    } catch {
    }
  }

  setEnvironment(options: MIEnvironmentOptions): void {
    this._environmentOptions = { ...this._environmentOptions, ...options };

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const scene = this._viewer.scene;

      if (options.skyBox !== undefined) {
        if (typeof options.skyBox === 'boolean') {
          scene.skyBox.show = options.skyBox;
        } else if (options.skyBox && options.skyBox.sources) {
          scene.skyBox = new Cesium.SkyBox({ sources: options.skyBox.sources });
        }
      }

      if (options.skyAtmosphere !== undefined) {
        scene.skyAtmosphere.show = options.skyAtmosphere;
      }

      if (options.sun !== undefined) {
        scene.sun.show = options.sun;
      }

      if (options.moon !== undefined) {
        scene.moon.show = options.moon;
      }

      if (options.groundColor !== undefined && scene.globe) {
        scene.globe.baseColor = Cesium.Color.fromCssColorString(options.groundColor);
      }
    } catch {
    }
  }

  setUnderground(options: MIUndergroundOptions): void {
    this._undergroundOptions = { ...this._undergroundOptions, ...options };

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const scene = this._viewer.scene;
      const globe = scene.globe;

      if (options.enabled !== undefined) {
        scene.screenSpaceCameraController.enableCollisionDetection = !options.enabled;
        globe.translucency.enabled = options.enabled;
      }

      if (options.transparency !== undefined && globe.translucency) {
        globe.translucency.frontFaceAlpha = 1 - options.transparency;
      }

      if (options.fadeDistance !== undefined && globe.translucency) {
        globe.translucency.fadeByDistance = new Cesium.NearFarScalar(
          0,
          1 - (options.transparency ?? 0),
          options.fadeDistance,
          1
        );
      }
    } catch {
    }
  }

  async getElevation(coord: [number, number]): Promise<number> {
    if (!this._cesiumAvailable || !this._viewer) return 0;

    try {
      const cartographic = Cesium.Cartographic.fromDegrees(coord[0], coord[1]);
      const terrainProvider = this._viewer.scene.terrainProvider;

      if (terrainProvider && terrainProvider.ready !== false) {
        const positions = await Cesium.sampleTerrainMostDetailed(terrainProvider, [cartographic]);
        return positions[0]?.height ?? 0;
      }
      return 0;
    } catch {
      return 0;
    }
  }

  screenToGeo(pixel: [number, number]): [number, number, number] | null {
    if (!this._cesiumAvailable || !this._viewer) return null;

    try {
      const cartesian = this._viewer.scene.pickPosition(
        new Cesium.Cartesian2(pixel[0], pixel[1])
      );
      if (!cartesian) {
        const ray = this._viewer.camera.getPickRay(new Cesium.Cartesian2(pixel[0], pixel[1]));
        if (!ray) return null;
        const globeCartesian = this._viewer.scene.globe.pick(ray, this._viewer.scene);
        return this._cartesianToLonLatAlt(globeCartesian);
      }
      return this._cartesianToLonLatAlt(cartesian);
    } catch {
      return null;
    }
  }

  geoToScreen(coord: [number, number, number]): [number, number] | null {
    if (!this._cesiumAvailable || !this._viewer) return null;

    try {
      const cartesian = this._lonLatAltToCartesian(coord[0], coord[1], coord[2]);
      const screenPos = Cesium.SceneTransforms.wgs84ToWindowCoordinates(
        this._viewer.scene,
        cartesian
      );
      if (!screenPos) return null;
      return [screenPos.x, screenPos.y];
    } catch {
      return null;
    }
  }

  /**
   * Normalise a raw Cesium pick result into the public MIGIS pick contract.
   * Consumers should never see Cesium primitive types in the return value.
   */
  private _toMIPickResult(picked: any, pixel: [number, number], coord: [number, number, number] | null): MIPickResult {
    let kind: MIPickResult['kind'] = 'unknown';
    if (picked?.id && (picked.id?.constructor?.name === 'Entity' || typeof picked.id === 'object')) kind = 'entity';
    else if (picked?.primitive && picked.primitive.constructor?.name?.includes('Tileset')) kind = 'tileset';
    else if (picked?.primitive && picked.primitive.constructor?.name?.includes('Imagery')) kind = 'imagery';
    else if (picked?.primitive) kind = 'primitive';
    return {
      kind,
      pixel,
      position: coord,
      layer: (picked?.id?._miLayer ?? picked?.primitive?._miLayer ?? null),
      id: picked?.id?.id ?? (typeof picked?.id === 'string' ? picked.id : null),
      raw: picked,
    };
  }

  pick(
    pixel: [number, number],
    options?: { layers?: any[]; tolerance?: number }
  ): MIPickResult | null {
    if (!this._cesiumAvailable || !this._viewer) return null;

    try {
      const cartesian2 = new Cesium.Cartesian2(pixel[0], pixel[1]);
      const width = options?.tolerance ?? 3;
      const picked = this._viewer.scene.pick(cartesian2, width, width);
      if (!picked) return null;

      const position = this._viewer.scene.pickPosition(cartesian2);
      const coord = this._cartesianToLonLatAlt(position);
      return this._toMIPickResult(picked, pixel, coord);
    } catch {
      return null;
    }
  }

  drillPick(
    pixel: [number, number],
    options?: { limit?: number; tolerance?: number }
  ): MIPickResult[] {
    if (!this._cesiumAvailable || !this._viewer) return [];

    try {
      const cartesian2 = new Cesium.Cartesian2(pixel[0], pixel[1]);
      const width = options?.tolerance ?? 3;
      const limit = options?.limit ?? 10;
      const picks = this._viewer.scene.drillPick(cartesian2, limit, width, width);

      return picks.map((picked: any) => this._toMIPickResult(picked, pixel, null));
    } catch {
      return [];
    }
  }

  addLayer(layer: any): void {
    this._layers.push(layer);
    // Translator pattern: if the layer knows how to attach itself to a Cesium
    // viewer, do that now. If Cesium is not yet available, the layer is held
    // in _layers and will be flushed on the next ready signal.
    if (this._cesiumAvailable && this._viewer && layer && typeof layer._applyToScene === 'function') {
      try { layer._applyToScene(this._viewer); }
      catch (err) { this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) }); }
    }
    this.emit('layeradd', { layer });
  }

  removeLayer(layer: any): void {
    const idx = this._layers.indexOf(layer);
    if (idx >= 0) {
      this._layers.splice(idx, 1);
      if (this._viewer && layer && typeof layer._removeFromScene === 'function') {
        try { layer._removeFromScene(this._viewer); }
        catch (err) { this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) }); }
      }
      this.emit('layerremove', { layer });
    }
  }

  clearLayers(): void {
    const layers = this._layers.slice();
    for (const lyr of layers) this.removeLayer(lyr);
  }

  getLayers(): any[] {
    this._syncAdoptedRawLayers();
    return [...this._layers];
  }

  /** Reflect entities, data sources, imagery and primitives already on an adopted viewer. */
  private _syncAdoptedRawLayers(): void {
    const viewer = this._adoptedRawViewer;
    if (!viewer) return;
    const rawItems: Array<{ id: string; item: any; remove: () => void; data: () => any; getFeatures: () => any[] }> = [];
    const normaliseFeatures = (data: any): any[] => {
      if (!data) return [];
      if (data.type === 'FeatureCollection' && Array.isArray(data.features)) return data.features;
      if (data.type === 'Feature') return [data];
      if (typeof data.type === 'string' && data.coordinates) {
        return [{ type: 'Feature', properties: {}, geometry: data }];
      }
      return [];
    };
    const entityData = (entity: any) => {
      try {
        const toLonLatAlt = (cartesian: any) => {
          const cartographic = cartesian && Cesium.Cartographic.fromCartesian(cartesian);
          return cartographic
            ? [Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude), cartographic.height]
            : null;
        };
        const properties: Record<string, any> = {};
        const names = entity.properties?.propertyNames || [];
        names.forEach((name: string) => { properties[name] = entity.properties[name]?.getValue?.(viewer.clock.currentTime); });
        const cartesian = entity.position?.getValue?.(viewer.clock.currentTime);
        const point = toLonLatAlt(cartesian);
        if (point) {
          return { type: 'Feature', properties, geometry: { type: 'Point', coordinates: point } };
        }
        const rectangle = entity.rectangle?.coordinates?.getValue?.(viewer.clock.currentTime);
        if (rectangle) {
          const west = Cesium.Math.toDegrees(rectangle.west);
          const south = Cesium.Math.toDegrees(rectangle.south);
          const east = Cesium.Math.toDegrees(rectangle.east);
          const north = Cesium.Math.toDegrees(rectangle.north);
          return { type: 'Feature', properties, geometry: { type: 'Polygon', coordinates: [[[west, south], [east, south], [east, north], [west, north], [west, south]]] } };
        }
        const positions = entity.polyline?.positions?.getValue?.(viewer.clock.currentTime);
        if (positions) {
          return { type: 'Feature', properties, geometry: { type: 'LineString', coordinates: positions.map(toLonLatAlt).filter(Boolean) } };
        }
        const hierarchy = entity.polygon?.hierarchy?.getValue?.(viewer.clock.currentTime);
        if (hierarchy?.positions) {
          const ring = hierarchy.positions.map(toLonLatAlt).filter(Boolean);
          if (ring.length) ring.push(ring[0]);
          return { type: 'Feature', properties, geometry: { type: 'Polygon', coordinates: [ring] } };
        }
        return undefined;
      } catch {
        return undefined;
      }
    };
    (viewer.entities?.values || []).forEach((entity: any, index: number) => {
      rawItems.push({
        id: `entity:${entity.id || index}`,
        item: entity,
        remove: () => viewer.entities.remove(entity),
        data: () => entityData(entity),
        getFeatures: () => {
          const feature = entityData(entity);
          return feature ? [feature] : [];
        },
      });
    });
    const dataSourceCount = viewer.dataSources?.length || 0;
    for (let index = 0; index < dataSourceCount; index++) {
      const dataSource = viewer.dataSources.get(index);
      const dataSourceFeatures = () => {
        const direct = normaliseFeatures(dataSource.data);
        if (direct.length) return direct;
        return (dataSource.entities?.values || []).map(entityData).filter(Boolean);
      };
      rawItems.push({
        id: `datasource:${dataSource.name || index}`,
        item: dataSource,
        remove: () => viewer.dataSources.remove(dataSource, true),
        data: () => ({ type: 'FeatureCollection', features: dataSourceFeatures() }),
        getFeatures: dataSourceFeatures,
      });
    }
    const imageryCount = viewer.imageryLayers?.length || 0;
    for (let index = 0; index < imageryCount; index++) {
      const imagery = viewer.imageryLayers.get(index);
      rawItems.push({
        id: `imagery:${imagery.imageryProvider?.url || index}`,
        item: imagery,
        remove: () => viewer.imageryLayers.remove(imagery, true),
        data: () => undefined,
        getFeatures: () => [],
      });
    }
    const primitiveCount = viewer.scene?.primitives?.length || 0;
    for (let index = 0; index < primitiveCount; index++) {
      const primitive = viewer.scene.primitives.get(index);
      rawItems.push({
        id: `primitive:${primitive.id || index}`,
        item: primitive,
        remove: () => viewer.scene.primitives.remove(primitive),
        data: () => undefined,
        getFeatures: () => [],
      });
    }
    const ids = new Set(rawItems.map((entry) => entry.id));
    this._layers = this._layers.filter((layer: any) => !layer.__adoptedRawLayerId || ids.has(layer.__adoptedRawLayerId));
    rawItems.forEach((entry) => {
      const existing = this._layers.find((layer: any) => layer.__adoptedRawLayerId === entry.id) as any;
      if (existing) {
        existing.__adoptedRawLayerState.entry = entry;
        existing.isVisible = () => entry.item.show !== false;
        existing.setVisible = (visible: boolean) => { entry.item.show = visible; viewer.scene?.requestRender?.(); };
        existing._removeFromScene = entry.remove;
        return;
      }
      const state = { entry };
      const source: any = {
        exportSourceId: `cesium:${entry.id}`,
        getFeatures: () => state.entry.getFeatures(),
      };
      Object.defineProperty(source, 'data', { enumerable: true, get: () => state.entry.data() });
      const layer: any = {
        id: entry.id,
        __adoptedRawLayerId: entry.id,
        __adoptedRawLayerState: state,
        exportSourceId: source.exportSourceId,
        getSource: () => source,
        getFeatures: source.getFeatures,
        isVisible: () => entry.item.show !== false,
        setVisible: (visible: boolean) => { entry.item.show = visible; viewer.scene?.requestRender?.(); },
        _removeFromScene: entry.remove,
      };
      this._layers.push(layer);
    });
  }

  /** @deprecated Use unstable_getCesiumViewer(). */
  getCesiumViewer(): any {
    if (!MISceneView._deprecationWarned.has('getCesiumViewer')) {
      MISceneView._deprecationWarned.add('getCesiumViewer');
      MIDiagnostics.warn('MISceneView', 'getCesiumViewer() is deprecated; use unstable_getCesiumViewer()');
    }
    return this._viewer;
  }

  setLightingTime(date: Date): void {
    this._lightingTime = new Date(date);

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      this._viewer.clock.currentTime = Cesium.JulianDate.fromDate(this._lightingTime);
    } catch {
    }
  }

  getLightingTime(): Date {
    if (this._cesiumAvailable && this._viewer) {
      try {
        const julianDate = this._viewer.clock.currentTime;
        this._lightingTime = Cesium.JulianDate.toDate(julianDate);
      } catch {
      }
    }
    return new Date(this._lightingTime);
  }

  enableSunTracking(speed: number = 1): void {
    this._sunTracking = true;
    this._sunTrackingSpeed = speed;

    if (this._sunTrackingInterval) {
      clearInterval(this._sunTrackingInterval);
    }

    this._sunTrackingInterval = setInterval(() => {
      if (!this._sunTracking) return;

      const currentTime = this.getLightingTime();
      const newTime = new Date(currentTime.getTime() + 60000 * this._sunTrackingSpeed);
      this.setLightingTime(newTime);
    }, 1000);
  }

  disableSunTracking(): void {
    this._sunTracking = false;
    if (this._sunTrackingInterval) {
      clearInterval(this._sunTrackingInterval);
      this._sunTrackingInterval = null;
    }
  }

  getSunPosition(): { azimuth: number; elevation: number } {
    if (!this._cesiumAvailable || !this._viewer) {
      return { azimuth: 0, elevation: 45 };
    }

    try {
      const julianDate = this._viewer.clock.currentTime;
      const sunPos = Cesium.Simon1994PlanetaryPositions.computeSunPositionInEarthInertialFrame(julianDate);
      const transform = Cesium.Transforms.computeTemeToPseudoFixedMatrix(julianDate);
      const sunFixed = Cesium.Matrix3.multiplyByVector(transform, sunPos, new Cesium.Cartesian3());
      const carto = Cesium.Cartographic.fromCartesian(sunFixed);

      return {
        azimuth: Cesium.Math.toDegrees(carto.longitude),
        elevation: Cesium.Math.toDegrees(carto.latitude),
      };
    } catch {
      return { azimuth: 0, elevation: 45 };
    }
  }

  addLight(light: MILight): string {
    const id = generateLightId();
    const storedLight: StoredLight = { ...light, _id: id };

    if (this._cesiumAvailable && this._viewer && Cesium) {
      try {
        const color = light.color
          ? Cesium.Color.fromCssColorString(light.color)
          : Cesium.Color.WHITE;
        const intensity = light.intensity ?? 1;

        if (light.type === 'directional') {
          const direction = light.direction
            ? new Cesium.Cartesian3(light.direction[0], light.direction[1], light.direction[2])
            : new Cesium.Cartesian3(0, 0, -1);
          const directionalLight = new Cesium.DirectionalLight({
            direction,
            color,
            intensity,
          });
          storedLight._cesiumLight = directionalLight;
          this._viewer.scene.light = directionalLight;
        }
      } catch {
      }
    }

    this._lights.push(storedLight);
    return id;
  }

  removeLight(lightId: string): boolean {
    const idx = this._lights.findIndex((l) => l._id === lightId);
    if (idx < 0) return false;

    const removed = this._lights.splice(idx, 1)[0];

    if (this._cesiumAvailable && this._viewer && removed._cesiumLight) {
      try {
        if (this._viewer.scene.light === removed._cesiumLight) {
          this._viewer.scene.light = new Cesium.SunLight();
        }
      } catch {
      }
    }

    return true;
  }

  getLights(): Array<MILight & { id: string }> {
    return this._lights.map((l) => {
      const { _id, _cesiumLight, ...rest } = l;
      return { ...rest, id: _id };
    });
  }

  setShadowOptions(options: MIShadowOptions): void {
    this._shadowOptions = { ...this._shadowOptions, ...options };

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      this._viewer.shadows = options.enabled;

      if (options.enabled && this._viewer.shadowMap) {
        const shadowMap = this._viewer.shadowMap;
        if (options.resolution !== undefined) {
          shadowMap.size = options.resolution;
        }
        if (options.softness !== undefined) {
          shadowMap.softShadows = options.type === 'soft' || options.softness > 0;
        }
        if (options.darkness !== undefined) {
          shadowMap.darkness = options.darkness;
        }
        if (options.cascades !== undefined) {
          shadowMap.numberOfCascades = options.cascades;
        }
        if (options.maxDistance !== undefined) {
          shadowMap.maximumDistance = options.maxDistance;
        }
      }

      const globe = this._viewer.scene.globe;
      if (options.terrainCastShadows !== undefined) {
        globe.shadows = options.terrainCastShadows
          ? Cesium.ShadowMode.CAST_ONLY
          : Cesium.ShadowMode.DISABLED;
      }
      if (options.terrainReceiveShadows !== undefined) {
        globe.shadows = options.terrainReceiveShadows
          ? Cesium.ShadowMode.RECEIVE_ONLY
          : globe.shadows;
      }
      if (options.terrainCastShadows && options.terrainReceiveShadows) {
        globe.shadows = Cesium.ShadowMode.ENABLED;
      }
    } catch {
    }
  }

  getShadowOptions(): MIShadowOptions {
    return { ...this._shadowOptions };
  }

  addWeatherEffect(effect: any): { id: string; remove: () => void } {
    const id = generateEffectId();

    if (this._cesiumAvailable && this._viewer && effect) {
      try {
        if (effect.type === 'rain' || effect.type === 'snow' || effect.type === 'fog') {
          this._weatherEffects.set(id, effect);
        }
      } catch {
      }
    } else {
      this._weatherEffects.set(id, effect);
    }

    return {
      id,
      remove: () => {
        this._weatherEffects.delete(id);
      },
    };
  }

  addWaterSurface(options: any): { id: string; remove: () => void } {
    const id = generateEffectId();

    if (this._cesiumAvailable && this._viewer && options) {
      try {
        this._waterSurfaces.set(id, options);
      } catch {
      }
    } else {
      this._waterSurfaces.set(id, options);
    }

    return {
      id,
      remove: () => {
        this._waterSurfaces.delete(id);
      },
    };
  }

  setPostProcessing(options: any): void {
    this._postProcessing = options;

    if (!this._cesiumAvailable || !this._viewer) return;

    try {
      const stages = this._viewer.scene.postProcessStages;

      if (options === null || options === false) {
        stages.removeAll();
        return;
      }

      if (options.bloom) {
        stages.bloom.enabled = true;
        if (options.bloom.intensity !== undefined) {
          stages.bloom.uniforms.glowOnly = false;
          stages.bloom.uniforms.brightness = options.bloom.intensity;
        }
      }

      if (options.ambientOcclusion) {
        stages.ambientOcclusion.enabled = true;
        if (options.ambientOcclusion.intensity !== undefined) {
          stages.ambientOcclusion.uniforms.intensity = options.ambientOcclusion.intensity;
        }
      }

      if (options.fxaa !== undefined) {
        stages.fxaa.enabled = options.fxaa;
      }
    } catch {
    }
  }

  getPostProcessing(): any {
    return this._postProcessing;
  }

  updateSize(): void {
    if (this._cesiumAvailable && this._viewer) {
      try {
        this._viewer.resize();
      } catch {
      }
    }
  }

  render(): void {
    if (this._cesiumAvailable && this._viewer) {
      try {
        this._viewer.scene.requestRender();
      } catch {
      }
    }
  }

  /**
   * Show the standalone MIGIS GIS toolbar over this scene. It is safe to call
   * this more than once; later calls update its position and drag setting.
   */
  showGISToolBar(options?: GISToolBarOptions): GISToolBar;
  /** Legacy positional form: `showGISToolBar({ x, y }, draggable)`. */
  showGISToolBar(offset?: GISToolBarOffset, draggable?: boolean): GISToolBar;
  showGISToolBar(
    optionsOrOffset: GISToolBarOptions | GISToolBarOffset = {},
    draggable?: boolean
  ): GISToolBar {
    const options: GISToolBarOptions = Array.isArray(optionsOrOffset)
      ? { offset: optionsOrOffset, draggable }
      : ('x' in optionsOrOffset && 'y' in optionsOrOffset)
        ? { offset: optionsOrOffset, draggable }
        : optionsOrOffset;
    if (this._gisToolBar) {
      this._gisToolBar.update(options);
      return this._gisToolBar;
    }
    this._gisToolBar = new GISToolBar(this, options);
    return this._gisToolBar;
  }

  /** Remove the GIS toolbar and dispose interactive work it owns. */
  hideGISToolBar(): void {
    this._gisToolBar?.destroy();
    this._gisToolBar = null;
  }

  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this.hideGISToolBar();

    this.disableSunTracking();

    if (this._orbitHandle) {
      clearInterval(this._orbitHandle);
      this._orbitHandle = null;
    }

    this._weatherEffects.clear();
    this._waterSurfaces.clear();
    this._lights = [];
    if (this._clockController) { try { this._clockController.destroy(); } catch {} this._clockController = null; }
    this._entityCollection = null;
    this._dataSourceCollection = null;
    this._imageryLayerCollection = null;

    // Detach any layers from the viewer before tearing it down so that
    // each layer's _removeFromScene() runs and the layer can be reattached
    // to a fresh MISceneView later.
    if (this._ownsCesiumViewer) this.clearLayers();

    if (this._ownsCesiumViewer && this._cesiumAvailable && this._viewer) {
      try {
        this._viewer.destroy();
      } catch {
      }
    }

    this._viewer = null;
    this.removeAllListeners();
    if (this._ownsCesiumViewer) this._container.innerHTML = '';
  }

  toJSON(): Record<string, any> {
    return {
      mode: this._mode,
      camera: { ...this._camera },
      navigationMode: this._navigationMode,
      terrain: this._terrainOptions ? { ...this._terrainOptions } : null,
      atmosphere: this._atmosphereOptions,
      environment: { ...this._environmentOptions },
      underground: { ...this._undergroundOptions },
      performance: { ...this._performanceOptions },
      postProcessing: this._postProcessing,
      shadowOptions: { ...this._shadowOptions },
      lightingTime: this._lightingTime.toISOString(),
      sunTracking: this._sunTracking,
      sunTrackingSpeed: this._sunTrackingSpeed,
      lights: this._lights.map((l) => {
        const { _id, _cesiumLight, ...rest } = l;
        return { ...rest, id: _id };
      }),
    };
  }

  static fromJSON(json: Record<string, any>): MISceneView {
    const target = json.target || document.createElement('div');
    const view = new MISceneView({
      target,
      mode: json.mode,
      camera: json.camera,
      navigationMode: json.navigationMode,
      terrain: json.terrain || undefined,
      atmosphere: json.atmosphere,
      environment: json.environment,
      underground: json.underground,
      performance: json.performance,
      postProcessing: json.postProcessing,
    });

    if (json.shadowOptions) {
      view.setShadowOptions(json.shadowOptions);
    }

    if (json.lightingTime) {
      view.setLightingTime(new Date(json.lightingTime));
    }

    if (json.sunTracking) {
      view.enableSunTracking(json.sunTrackingSpeed ?? 1);
    }

    if (json.lights) {
      for (const light of json.lights) {
        const { id, ...rest } = light;
        view.addLight(rest);
      }
    }

    return view;
  }

  // ─── Collections (Step 5) ──────────────────────────────────────────────
  private _entityCollection: MIEntityCollection | null = null;
  private _dataSourceCollection: MIDataSourceCollection | null = null;
  private _imageryLayerCollection: MIImageryLayerCollection | null = null;
  private _clockController: MIClockController | null = null;

  get entities(): MIEntityCollection {
    if (!this._entityCollection) {
      this._entityCollection = new MIEntityCollection(this._viewer?.entities ?? null);
    }
    return this._entityCollection;
  }

  get dataSources(): MIDataSourceCollection {
    if (!this._dataSourceCollection) {
      this._dataSourceCollection = new MIDataSourceCollection(this._viewer?.dataSources ?? null);
    }
    return this._dataSourceCollection;
  }

  get imageryLayers(): MIImageryLayerCollection {
    if (!this._imageryLayerCollection) {
      this._imageryLayerCollection = new MIImageryLayerCollection(this._viewer?.imageryLayers ?? null);
    }
    return this._imageryLayerCollection;
  }

  // ─── Scene mode + widgets (Step 6) ─────────────────────────────────────
  getSceneMode(): '2d' | 'columbus' | '3d' {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return '3d';
    const m = this._viewer.scene.mode;
    if (m === Cesium.SceneMode.SCENE2D) return '2d';
    if (m === Cesium.SceneMode.COLUMBUS_VIEW) return 'columbus';
    return '3d';
  }
  setSceneMode(mode: '2d' | 'columbus' | '3d', durationSec: number = 0): void {
    if (mode === '2d') this.morphTo2D(durationSec);
    else if (mode === 'columbus') this.morphToColumbusView(durationSec);
    else this.morphTo3D(durationSec);
  }
  morphTo2D(durationSec: number = 2): void { try { this._viewer?.scene?.morphTo2D?.(durationSec); } catch {} }
  morphTo3D(durationSec: number = 2): void { try { this._viewer?.scene?.morphTo3D?.(durationSec); } catch {} }
  morphToColumbusView(durationSec: number = 2): void { try { this._viewer?.scene?.morphToColumbusView?.(durationSec); } catch {} }

  /**
   * Toggle a Cesium Viewer widget's visibility.
   *
   * Note: this is a *visibility* toggle (DOM `display`), not a widget
   * lifecycle reconfiguration — the underlying widget remains
   * instantiated and continues to be wired into the viewer. To omit a
   * widget entirely, pass the corresponding option as `false` when the
   * Cesium `Viewer` is first constructed.
   */
  setWidgetVisible(widget:
    | 'baseLayerPicker' | 'geocoder' | 'fullscreenButton' | 'vrButton'
    | 'sceneModePicker' | 'navigationHelpButton' | 'projectionPicker'
    | 'infoBox' | 'selectionIndicator' | 'homeButton' | 'animation' | 'timeline',
    visible: boolean
  ): void {
    if (!this._viewer) return;
    // Cesium exposes each widget at viewer[name].container — fall back to
    // viewer[`${name}Container`] for older builds that flattened the tree.
    const v: any = this._viewer;
    const widgetObj = v[widget];
    const container =
      (widgetObj && widgetObj.container) ||
      v[`${widget}Container`] ||
      null;
    if (container?.style) container.style.display = visible ? '' : 'none';
  }
  // Per-widget setters (preferred public API — match Cesium Viewer option names 1:1)
  setBaseLayerPickerVisible(v: boolean): void { this.setWidgetVisible('baseLayerPicker', v); }
  setGeocoderVisible(v: boolean): void { this.setWidgetVisible('geocoder', v); }
  setFullscreenButtonVisible(v: boolean): void { this.setWidgetVisible('fullscreenButton', v); }
  setVrButtonVisible(v: boolean): void { this.setWidgetVisible('vrButton', v); }
  setSceneModePickerVisible(v: boolean): void { this.setWidgetVisible('sceneModePicker', v); }
  setNavigationHelpButtonVisible(v: boolean): void { this.setWidgetVisible('navigationHelpButton', v); }
  setProjectionPickerVisible(v: boolean): void { this.setWidgetVisible('projectionPicker', v); }
  setInfoBoxVisible(v: boolean): void { this.setWidgetVisible('infoBox', v); }
  setSelectionIndicatorVisible(v: boolean): void { this.setWidgetVisible('selectionIndicator', v); }
  setHomeButtonVisible(v: boolean): void { this.setWidgetVisible('homeButton', v); }
  setAnimationVisible(v: boolean): void { this.setWidgetVisible('animation', v); }
  setTimelineVisible(v: boolean): void { this.setWidgetVisible('timeline', v); }

  // ─── Clock + tracking + selection (Step 7) ─────────────────────────────
  get clock(): MIClockController {
    if (!this._clockController) this._clockController = new MIClockController(this._viewer?.clock ?? null);
    return this._clockController;
  }
  get trackedEntity(): any { return this._viewer?.trackedEntity ?? null; }
  set trackedEntity(e: any) { if (this._viewer) this._viewer.trackedEntity = e; }
  get selectedEntity(): any { return this._viewer?.selectedEntity ?? null; }
  set selectedEntity(e: any) { if (this._viewer) this._viewer.selectedEntity = e; }
  flyToTarget(target: any, options?: { duration?: number; offset?: MICameraOrientation }): Promise<boolean> {
    if (!this._viewer?.flyTo) return Promise.resolve(false);
    const offset = options?.offset && Cesium
      ? new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(options.offset.heading),
          Cesium.Math.toRadians(options.offset.pitch),
          0
        )
      : undefined;
    return Promise.resolve(this._viewer.flyTo(target, {
      duration: (options?.duration ?? 3000) / 1000,
      offset,
    }));
  }
  zoomToTarget(target: any): Promise<boolean> {
    if (!this._viewer?.zoomTo) return Promise.resolve(false);
    return Promise.resolve(this._viewer.zoomTo(target));
  }

  // ─── Picking + globe queries (Step 8) ──────────────────────────────────
  pickPosition(pixel: [number, number]): [number, number, number] | null {
    return this.screenToGeo(pixel);
  }
  /**
   * Cast a ray through the scene and return the first pick.
   *
   * Coordinate conventions:
   *   - `origin`    : `[longitude_degrees, latitude_degrees, height_metres]`
   *                   (interpreted via `Cesium.Cartesian3.fromDegrees`).
   *   - `direction` : a Cartesian world-space unit vector `[x, y, z]`
   *                   in metres (NOT degrees). Caller is expected to
   *                   build this with `MIGeoMath` or
   *                   `unstable_getCesiumViewer().scene` helpers when
   *                   geographic-relative direction is required.
   */
  pickFromRay(origin: [number, number, number], direction: [number, number, number]): MIPickResult | null {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return null;
    try {
      const o = Cesium.Cartesian3.fromDegrees(origin[0], origin[1], origin[2]);
      const d = new Cesium.Cartesian3(direction[0], direction[1], direction[2]);
      const ray = new Cesium.Ray(o, d);
      const picked = this._viewer.scene.pickFromRay(ray);
      if (!picked) return null;
      const coord = picked.position ? this._cartesianToLonLatAlt(picked.position) : null;
      return this._toMIPickResult(picked.object ?? picked, [-1, -1], coord);
    } catch (e) {
      MIDiagnostics.warn('MISceneView', 'pickFromRay() failed', e as Error);
      return null;
    }
  }
  getGlobeHeight(lon: number, lat: number): number | null {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return null;
    try {
      const carto = Cesium.Cartographic.fromDegrees(lon, lat);
      const h = this._viewer.scene.globe.getHeight(carto);
      return typeof h === 'number' ? h : null;
    } catch { return null; }
  }
  sampleHeight(lonLat: [number, number]): number | null {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return null;
    try {
      const carto = Cesium.Cartographic.fromDegrees(lonLat[0], lonLat[1]);
      return this._viewer.scene.sampleHeight?.(carto) ?? null;
    } catch { return null; }
  }
  /**
   * Clamp a lon/lat to the closest 3D Tiles / model surface using
   * `scene.clampToHeight` when available, falling back to terrain
   * sampling via `scene.sampleHeight`.
   */
  clampToHeight(lonLat: [number, number]): [number, number, number] | null {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return null;
    try {
      const scene: any = this._viewer.scene;
      const carto = Cesium.Cartographic.fromDegrees(lonLat[0], lonLat[1]);
      // Prefer real geometry-clamping if Cesium exposes it (clamps to 3D Tiles + models).
      if (typeof scene.clampToHeight === 'function') {
        const carteIn = Cesium.Cartesian3.fromDegrees(lonLat[0], lonLat[1], 0);
        const clamped = scene.clampToHeight(carteIn);
        if (clamped) {
          const c = Cesium.Cartographic.fromCartesian(clamped);
          return [lonLat[0], lonLat[1], c.height];
        }
      }
      const h = scene.sampleHeight?.(carto);
      return (h === undefined || h === null) ? null : [lonLat[0], lonLat[1], h];
    } catch { return null; }
  }
  async sampleHeightMostDetailed(lonLats: [number, number][]): Promise<(number | null)[]> {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return lonLats.map(() => null);
    try {
      const cartos = lonLats.map((p) => Cesium.Cartographic.fromDegrees(p[0], p[1]));
      const out = await this._viewer.scene.sampleHeightMostDetailed(cartos);
      return out.map((c: any) => c?.height ?? null);
    } catch { return lonLats.map(() => null); }
  }
  /**
   * Async batch clamp using `scene.clampToHeightMostDetailed` when present,
   * otherwise falls back to terrain `sampleHeightMostDetailed`.
   */
  async clampToHeightMostDetailed(lonLats: [number, number][]): Promise<([number, number, number] | null)[]> {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return lonLats.map(() => null);
    const scene: any = this._viewer.scene;
    if (typeof scene.clampToHeightMostDetailed === 'function') {
      try {
        const carteIns = lonLats.map((p) => Cesium.Cartesian3.fromDegrees(p[0], p[1], 0));
        const clamped: any[] = await scene.clampToHeightMostDetailed(carteIns);
        return clamped.map((c, i) => {
          if (!c) return null;
          const cg = Cesium.Cartographic.fromCartesian(c);
          return [lonLats[i][0], lonLats[i][1], cg.height] as [number, number, number];
        });
      } catch { /* fall through to terrain sampling */ }
    }
    const heights = await this.sampleHeightMostDetailed(lonLats);
    return lonLats.map((p, i) => heights[i] === null ? null : [p[0], p[1], heights[i]!] as [number, number, number]);
  }
  // Globe controls
  setGlobeShow(v: boolean): void { if (this._viewer?.scene?.globe) this._viewer.scene.globe.show = v; }
  setGlobeBaseColor(cssColor: string): void {
    if (this._viewer?.scene?.globe && Cesium) {
      try { this._viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString(cssColor); } catch {}
    }
  }
  setDepthTestAgainstTerrain(v: boolean): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.depthTestAgainstTerrain = v;
  }
  setGroundAtmosphere(v: boolean): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.showGroundAtmosphere = v;
  }
  setDynamicAtmosphereLighting(v: boolean): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.dynamicAtmosphereLighting = v;
  }
  setPreloadAncestors(v: boolean): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.preloadAncestors = v;
  }
  setPreloadSiblings(v: boolean): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.preloadSiblings = v;
  }
  setTileCacheSize(n: number): void {
    if (this._viewer?.scene?.globe) this._viewer.scene.globe.tileCacheSize = n;
  }
  addGlobeClippingPlane(plane: { normal: [number, number, number]; distance: number }): void {
    if (!this._viewer?.scene?.globe || !Cesium) return;
    try {
      const cp = new Cesium.ClippingPlane(new Cesium.Cartesian3(plane.normal[0], plane.normal[1], plane.normal[2]), plane.distance);
      if (!this._viewer.scene.globe.clippingPlanes) {
        this._viewer.scene.globe.clippingPlanes = new Cesium.ClippingPlaneCollection({ planes: [cp] });
      } else this._viewer.scene.globe.clippingPlanes.add(cp);
    } catch {}
  }
  clearGlobeClippingPlanes(): void {
    try { this._viewer?.scene?.globe?.clippingPlanes?.removeAll?.(); } catch {}
  }

  // ─── Camera HPR + render-loop knobs (Step 9) ───────────────────────────
  setViewWithOrientation(target: [number, number, number], orientation: MICameraOrientation): void {
    if (!this._cesiumAvailable || !this._viewer || !Cesium) return;
    try {
      this._viewer.camera.setView({
        destination: this._lonLatAltToCartesian(target[0], target[1], target[2]),
        orientation: {
          heading: Cesium.Math.toRadians(orientation.heading),
          pitch: Cesium.Math.toRadians(orientation.pitch),
          roll: Cesium.Math.toRadians(orientation.roll),
        },
      });
    } catch {}
  }
  lookAtTransform(matrix: any): void {
    try { this._viewer?.camera?.lookAtTransform?.(matrix); } catch {}
  }
  moveForward(d: number): void  { try { this._viewer?.camera?.moveForward?.(d);  } catch {} }
  moveBackward(d: number): void { try { this._viewer?.camera?.moveBackward?.(d); } catch {} }
  moveLeft(d: number): void     { try { this._viewer?.camera?.moveLeft?.(d);     } catch {} }
  moveRight(d: number): void    { try { this._viewer?.camera?.moveRight?.(d);    } catch {} }
  moveUp(d: number): void       { try { this._viewer?.camera?.moveUp?.(d);       } catch {} }
  moveDown(d: number): void     { try { this._viewer?.camera?.moveDown?.(d);     } catch {} }
  twistLeft(angleDeg: number): void  { try { this._viewer?.camera?.twistLeft?.(Cesium?.Math?.toRadians(angleDeg) ?? 0);  } catch {} }
  twistRight(angleDeg: number): void { try { this._viewer?.camera?.twistRight?.(Cesium?.Math?.toRadians(angleDeg) ?? 0); } catch {} }
  rotateLeft(angleDeg: number): void { try { this._viewer?.camera?.rotateLeft?.(Cesium?.Math?.toRadians(angleDeg) ?? 0); } catch {} }
  rotateRight(angleDeg: number): void{ try { this._viewer?.camera?.rotateRight?.(Cesium?.Math?.toRadians(angleDeg) ?? 0); } catch {} }
  rotateUp(angleDeg: number): void   { try { this._viewer?.camera?.rotateUp?.(Cesium?.Math?.toRadians(angleDeg) ?? 0); } catch {} }
  rotateDown(angleDeg: number): void { try { this._viewer?.camera?.rotateDown?.(Cesium?.Math?.toRadians(angleDeg) ?? 0); } catch {} }
  getPickRay(pixel: [number, number]): any | null {
    if (!this._viewer || !Cesium) return null;
    try { return this._viewer.camera.getPickRay(new Cesium.Cartesian2(pixel[0], pixel[1])); }
    catch { return null; }
  }
  pickEllipsoid(pixel: [number, number]): [number, number, number] | null {
    if (!this._viewer || !Cesium) return null;
    try {
      const c = this._viewer.camera.pickEllipsoid(new Cesium.Cartesian2(pixel[0], pixel[1]));
      return c ? this._cartesianToLonLatAlt(c) : null;
    } catch { return null; }
  }
  computeViewRectangle(): { west: number; south: number; east: number; north: number } | null {
    if (!this._viewer || !Cesium) return null;
    try {
      const r = this._viewer.camera.computeViewRectangle();
      if (!r) return null;
      return {
        west: Cesium.Math.toDegrees(r.west), south: Cesium.Math.toDegrees(r.south),
        east: Cesium.Math.toDegrees(r.east), north: Cesium.Math.toDegrees(r.north),
      };
    } catch { return null; }
  }
  setRequestRenderMode(enabled: boolean, maximumRenderTimeChange?: number): void {
    if (!this._viewer?.scene) return;
    this._viewer.scene.requestRenderMode = enabled;
    if (maximumRenderTimeChange !== undefined) this._viewer.scene.maximumRenderTimeChange = maximumRenderTimeChange;
  }
  setMSAA(samples: 1 | 2 | 4 | 8): void { if (this._viewer?.scene) this._viewer.scene.msaaSamples = samples; }
  setFXAA(enabled: boolean): void {
    try { if (this._viewer?.scene?.postProcessStages?.fxaa) this._viewer.scene.postProcessStages.fxaa.enabled = enabled; } catch {}
  }
  showFramesPerSecond(v: boolean): void {
    if (this._viewer?.scene) this._viewer.scene.debugShowFramesPerSecond = v;
  }

  // ─── Engine escape hatches (Step 11) ───────────────────────────────────
  /** Escape hatch — direct Cesium viewer access. Use only when MIGIS lacks coverage. */
  unstable_getCesiumViewer(): any { return this._viewer; }

  /**
   * Escape hatch — returns the entire `Cesium` namespace (`Cartesian3`,
   * `Color`, `UrlTemplateImageryProvider`, etc.). Use ONLY for ancillary
   * helper classes the SDK does not yet wrap. Emits a one-time
   * MIDiagnostics warning per process.
   */
  static unstable_getCesiumNamespace(): any {
    if (!MISceneView._deprecationWarned.has('unstable_getCesiumNamespace')) {
      MISceneView._deprecationWarned.add('unstable_getCesiumNamespace');
      MIDiagnostics.warn(
        'MISceneView',
        'unstable_getCesiumNamespace() is an escape hatch — prefer SDK wrappers when available'
      );
    }
    return getCesium();
  }

  get viewer(): any {
    if (!MISceneView._deprecationWarned.has('viewer')) {
      MISceneView._deprecationWarned.add('viewer');
      MIDiagnostics.warn('MISceneView', '`viewer` getter is deprecated; use unstable_getCesiumViewer()');
    }
    return this._viewer;
  }
  private static _deprecationWarned: Set<string> = new Set();

  get cesiumAvailable(): boolean {
    return this._cesiumAvailable;
  }

  get initialized(): boolean {
    return this._initialized;
  }
}
